import { SideEffectManager } from "side-effect-manager";
import { UserStore, useUserStore } from "../user-store";
import { IServiceTextChat, IServiceVideoChat, NetworkEventData } from "@obsbot/remo-services";
import { ChannelItem, channelStore, useChannelStore } from "../channel-store";
import { create } from "zustand";
import { useGlobalStore } from "../global-store";
import { eventBus } from "@obsbot/remo-utils";
import { getPreferencesStore } from "../preferences-store";

export interface InitChannelroomStoreConfig {
    roomUUID: string;
    rtc: IServiceVideoChat;
    rtm: IServiceTextChat;
}

export enum SpecialEffectTo {
    All = "all",
    User = "user",
}
export interface InterActionMessageType {
    icon: string;
    toType: SpecialEffectTo;
    to: string;
    toName?: string;
    sender: string;
    actionID: string;
    soundEffect?: string;
}

// 该仓库用于调用底层腾讯SDK, channel-store用于调用请求
export interface ChannelRoomStoreState {
    /**
     * 副作用管理
     */
    sideEffect: SideEffectManager;
    /**
     * 频道房间UUID
     */
    readonly roomUUID: string;
    /**
     * 用户UUID
     */
    readonly userUUID: string;
    /**
     * 频道owner
     */
    ownerName: string;
    /**
     * 频道名称
     */
    channelName: string;
    /**
     * 是否使用TRTC加入房间
     */
    isJoinedRTC: boolean;
    /**
     * 是否正在加入房间
     */
    isLoadingRTC: boolean;
    /**
     * 频道内的成员
     */
    channelUsers: string[];
    /**
     * 当前用户屏蔽的成员
     */
    blockedUsers: string[];
    updateBlockedUsers: (blockedUsers: string[]) => void;
    /**
     * 频道内的特效互动
     */
    channelSpecialList: InterActionMessageType[];
    /**
     * 频道内的音频互动
     */
    channelAudioList: Array<Pick<InterActionMessageType, "soundEffect" | "actionID">>;
    // denoise: boolean;
    /**
     * 用户的仓库 {@link UserStore}
     */
    readonly users: UserStore | null;
    /**
     * RTC实例 {@link IServiceVideoChat}
     */
    readonly rtc: IServiceVideoChat | null;
    /**
     * RTM实例 {@link IServiceTextChat}
     */
    readonly rtm: IServiceTextChat | null;
    /**
     * 是否在游戏覆盖窗口
     */
    isInGameoverlay: boolean;
    /**
     * RTC的uid
     */
    rtcUID?: string;
    /**
     * 是否进入了频道
     */
    isEnterRoom: boolean;
    /**
     * 获取当前频道信息
     */
    roomInfo(): ChannelItem | undefined;

    joinRoom: (roomUUID: string, userUUID: string, roomInfo: any) => Promise<void>;
    leaveRoom: () => Promise<void>;

    // setDenoise: (denoise: boolean) => void;
    /**
     * 切换摄像头
     */
    /**
     * init 初始化操作, 包含加入频道、副作用管理等操作
     */
    init(): Promise<void>;
    /**
     * 初始化仓库
     */
    initStore(config: InitChannelroomStoreConfig): ChannelRoomStoreState;
    /**
     * 销毁操作
     */
    destroy(): Promise<void>;
    /**
     * 更新是否正在游戏覆盖窗口
     */
    updateIsInGameoverlay: (isInGameoverlay: boolean) => void;
    /**
     * 更新频道内的特效列表
     */
    updateSpecialList: (specialList: InterActionMessageType[]) => void;
    /**
     * 更新是否进入了频道
     */
    updateIsEnterRoom: (isSetOpenMic: boolean) => void;
    /**
     * 更新频道内的音频列表
     */
    updateAudioList: (
        audioList: Array<Pick<InterActionMessageType, "soundEffect" | "actionID">>,
    ) => void;
    /**
     * 追加频道内的音频列表
     */
    appendAudioList: (audio: Pick<InterActionMessageType, "soundEffect" | "actionID">) => void;
    /**
     * 移除频道内的音频列表
     */
    removeAudioList: (audio: Pick<InterActionMessageType, "soundEffect" | "actionID">) => void;
}

export const useChannelRoomStore = create<ChannelRoomStoreState>((set, get) => {
    const initRTC = async (): Promise<void> => {
        const { sideEffect, rtc, roomUUID, userUUID } = get();
        if (!rtc) {
            throw new Error("Missing rtc instance");
        }
        sideEffect.add(() => {
            const handleNetwork = (networks: NetworkEventData[]): void => {
                networks.forEach(network => {
                    const { userId, quality } = network;
                    let qualityObj: {
                        delay: number;
                        uplink: number;
                        downlink: number;
                    } = {
                        delay: 0,
                        uplink: 0,
                        downlink: 0,
                    };
                    if (quality === 0) {
                        qualityObj = {
                            delay: 1000,
                            uplink: 0,
                            downlink: 0,
                        };
                    } else if (quality === 1) {
                        qualityObj = {
                            delay: 49,
                            uplink: 49,
                            downlink: 49,
                        };
                    } else if (quality === 2) {
                        qualityObj = {
                            delay: 99,
                            uplink: 99,
                            downlink: 99,
                        };
                    } else if (quality <= 4) {
                        qualityObj = {
                            delay: 199,
                            uplink: 199,
                            downlink: 199,
                        };
                    } else {
                        qualityObj = {
                            delay: 201,
                            uplink: 201,
                            downlink: 201,
                        };
                    }
                    const { users } = get();
                    const uid = userId || userUUID;
                    const user = users?.cachedUsers.get(uid);
                    if (user && user.networkQuality.delay !== qualityObj.delay) {
                        users?.updateNetworkQuality(uid, qualityObj);
                    }
                });
            };

            rtc.events.on("network", handleNetwork);
            return () => {
                rtc.events.off("network", handleNetwork);
            };
        });
        // 处理用户状态改变
        sideEffect.add(() => {
            const handleUpdateState = async ({
                userID,
                shouldCamera,
                shouldMic,
            }: {
                userID: string;
                shouldCamera?: boolean;
                shouldMic?: boolean;
            }): Promise<void> => {
                console.log(
                    "[Store] [LOG] 接收到用户状态改变事件",
                    userID,
                    shouldCamera,
                    shouldMic,
                );
                const { users } = get();
                let currentUser = users!.cachedUsers.get(userID)!;
                if (currentUser) {
                    if (shouldCamera !== undefined) {
                        currentUser = { ...currentUser, camera: shouldCamera };
                        users?.cacheUser(currentUser);
                        if (shouldCamera) {
                            const avatar = rtc.getAvatar(userID);
                            avatar?.refreshAvatar();
                        }
                    }
                    if (shouldMic !== undefined) {
                        currentUser = { ...currentUser, mic: shouldMic };
                        users?.cacheUser(currentUser);
                    }
                } else {
                    const user = (await users!.createUsers([userID]))[0];
                    if (shouldCamera !== undefined) {
                        currentUser = { ...user, camera: shouldCamera };
                    }
                    if (shouldMic !== undefined) {
                        currentUser = { ...user, mic: shouldMic };
                    }
                    console.log("[Store] [LOG] 用户不存在于cachedUsers, 缓存新用户", currentUser);
                    users?.cacheUser(currentUser);
                }
            };
            rtc.events.on("update-state", handleUpdateState);
            return () => {
                rtc.events.off("update-state", handleUpdateState);
            };
        });
        const SPEAK_INTERVAL = 1000;
        let speakTimer: NodeJS.Timeout | null = null;
        sideEffect.addDisposer(
            rtc.events.on("volume-level-changed", volume => {
                const { users } = get();
                const currentUser = users!.cachedUsers.get(userUUID)!;
                if (volume > 0) {
                    if (!currentUser?.isSpeak) {
                        users?.cacheUser({ ...currentUser, mic: true, isSpeak: true });
                    }

                    if (speakTimer) {
                        clearTimeout(speakTimer);
                        speakTimer = null;
                    }
                }
                speakTimer = setTimeout(() => {
                    currentUser && users?.cacheUser({ ...currentUser, isSpeak: false });
                }, SPEAK_INTERVAL);
            }),
        );
        // 处理远端用户音频改变
        sideEffect.addDisposer(
            rtc.events.on("user-mic-level-changed", ({ userID, level }) => {
                const { users } = get();
                const currentUser = users!.cachedUsers.get(userID);
                if (!currentUser) {
                    return;
                }
                if (level > 0) {
                    if (!currentUser?.isSpeak) {
                        users?.cacheUser({ ...currentUser, isSpeak: true });
                    }

                    if (speakTimer) {
                        clearTimeout(speakTimer);
                        speakTimer = null;
                    }
                }
                speakTimer = setTimeout(() => {
                    currentUser && users?.cacheUser({ ...currentUser, isSpeak: false });
                }, SPEAK_INTERVAL);
            }),
        );
        // 处理远端用户离开房间
        sideEffect.addDisposer(
            rtc.events.on("user-leave-channel", async userID => {
                const { users } = get();
                eventBus.emit("user-leave-channel", userID);
                if (users?.cachedUsers.has(userID)) {
                    users.removeUser(userID);
                }
            }),
        );
        const globalStore = useGlobalStore.getState();
        if (globalStore.rtcInfo) {
            const performanceStore = getPreferencesStore(globalStore.rtcInfo.uid);
            await rtc.joinRoom({
                roomUUID: roomUUID,
                uid: globalStore.rtcInfo.uid,
                userSig: globalStore.rtcInfo.userSig,
                isTurnListenOn: useChannelStore.getState().userState.isListen,
                isEnableAES: performanceStore.enabledEchoCancellation,
                isEnableAGC: performanceStore.autoGainControl,
            });
            !get().isJoinedRTC && eventBus.emit("refresh-local-avatar", undefined);
            set({ roomUUID: roomUUID });
        }
    };
    return {
        sideEffect: new SideEffectManager(),
        roomUUID: "",
        channelName: "",
        ownerUUID: "",
        ownerName: "",
        isJoinedRTC: false,
        isLoadingRTC: false,
        // denoise: false,
        users: null,
        rtc: null,
        rtm: null,
        rtcUID: "",
        userUUID: "",
        isInGameoverlay: false,
        channelUsers: [],
        blockedUsers: [],
        channelSpecialList: [],
        isEnterRoom: false,
        channelAudioList: [],
        updateBlockedUsers: (users: string[]) => {
            set({
                blockedUsers: users,
            });
        },
        updateIsInGameoverlay(isInGameoverlay: boolean) {
            set({ isInGameoverlay });
        },
        initStore(config) {
            const globalStore = useGlobalStore.getState();
            if (!globalStore.userInfo?.user_id) {
                throw new Error("Missing user uuid");
            }
            (window as any).channelroomStore = this;
            const syncUserStore = (): void => {
                const userStore = useUserStore.getState();
                set({ users: userStore });
            };
            const unsubscribe = useUserStore.subscribe(syncUserStore);
            useUserStore.getState().initUserStore({
                roomUUID: config.roomUUID,
                userUUID: globalStore.userInfo.user_id,
            });
            set({
                userUUID: globalStore.userInfo.user_id,
                roomUUID: config.roomUUID,
                rtc: config.rtc,
                rtm: config.rtm,
                users: useUserStore.getState(),
            });
            useChannelStore.getState().updateUserState({
                userUUID: globalStore.userInfo.user_id,
            });
            this.sideEffect.addDisposer(() => unsubscribe());
            return this;
        },
        roomInfo() {
            return useChannelStore.getState().channels.get(this.roomUUID);
        },
        async init() {
            try {
                const { users, rtm, roomUUID, userUUID } = get();
                if (!users) {
                    throw new Error("Missing user store");
                }
                if (!rtm) {
                    throw new Error("Missing Tencent RTM service");
                }
                const globalStore = useGlobalStore.getState();
                if (!globalStore.rtcInfo) {
                    throw new Error("Missing User Info");
                }
                users.initCurrentUser(userUUID);
                set({ isLoadingRTC: true });
                const queryResult = await channelStore.queryChannelInfo(roomUUID);
                if (!queryResult) {
                    eventBus.emit("update-channels", undefined);
                    eventBus.emit("show-message", {
                        message: (window as any).__ReMoI18n.t("channel-not-exist"),
                        type: "warning",
                    });
                    window.history.back();
                    set({ isLoadingRTC: false });
                    return;
                }
                // 这里清理了会导致重新进入后远端视频丢失
                this.sideEffect.add(() => {
                    const handleNewMember = async (): Promise<void> => {
                        const queryResult = await channelStore.queryChannelNum(roomUUID);
                        console.log("[WEB] [LOG] 接收到new-member事件, 查询后端", queryResult);
                        const channelUsers = queryResult.data;
                        users.cachedUsers.forEach((_member, userId) => {
                            if (!channelUsers.includes(userId)) {
                                users.cachedUsers.delete(userId);
                            }
                        });
                        const newMembers = channelUsers;
                        const profiles = newMembers.length
                            ? await rtm.getUserProfile(newMembers)
                            : [];
                        users.cacheUsers(
                            profiles.map(profile => {
                                const userState = useChannelStore.getState().userState;
                                const users = useChannelRoomStore.getState().users?.cachedUsers;
                                if (profile.userID === userState.userUUID) {
                                    return {
                                        userUUID: profile.userID,
                                        rtcUID: profile.userID,
                                        avatar: profile.avatar,
                                        name: profile.nick,
                                        camera: userState?.camera || false,
                                        mic: userState?.mic || false,
                                        isSpeak: false,
                                        hasLeft: false,
                                        isListen: userState?.isListen || false,
                                        networkQuality: {
                                            delay: 0,
                                            uplink: 0,
                                            downlink: 0,
                                        },
                                    };
                                } else {
                                    return {
                                        userUUID: profile.userID,
                                        rtcUID: profile.userID,
                                        avatar: profile.avatar,
                                        name: profile.nick,
                                        camera: users?.get(profile.userID)?.camera || false,
                                        mic: users?.get(profile.userID)?.mic || false,
                                        isSpeak: users?.get(profile.userID)?.isSpeak || false,
                                        hasLeft: false,
                                        isListen: users?.get(profile.userID)?.isListen || false,
                                        networkQuality: {
                                            delay: 0,
                                            uplink: 0,
                                            downlink: 0,
                                        },
                                    };
                                }
                            }),
                        );
                    };
                    rtm.events.on("get-new-member", handleNewMember);
                    return () => {
                        rtm.events.off("get-new-member", handleNewMember);
                    };
                });
                this.sideEffect.add(() => {
                    const handleStatusChange = async ({
                        status,
                        channelUUID,
                    }: {
                        status: "deleted" | "rename";
                        channelUUID: string;
                    }): Promise<void> => {
                        console.log("[RTM] 频道状态发生改变", status);
                        const isSameChannel = get().roomUUID === channelUUID;
                        if (status === "deleted" && isSameChannel) {
                            await get().leaveRoom();
                            window.location.hash = "#/channel";
                            setTimeout(() => {
                                eventBus.emit("preview-camera", {
                                    isPreview: false,
                                });
                            }, 0);
                        }
                        if (status === "rename" && isSameChannel) {
                            const info = await channelStore.queryChannelInfo(channelUUID);
                            channelStore.updateActiveChannel({
                                ...info,
                            });
                        }
                        eventBus.emit("update-channels", undefined);
                    };
                    rtm.events.on("channel-status-change", handleStatusChange);
                    return () => {
                        rtm.events.off("channel-status-change", handleStatusChange);
                    };
                });
                this.sideEffect.add(() => {
                    const handleSetMessageInterval = (): void => {
                        const interval = queryResult.timeInterval;
                        if (interval !== undefined && interval !== null) {
                            (rtm as any).messageInterval = interval || 0;
                        }
                    };
                    rtm.events.on("get-message-interval", handleSetMessageInterval);
                    return () => {
                        rtm.events.off("get-message-interval", handleSetMessageInterval);
                    };
                });
                // 加入房间逻辑拆离到joinRoom方法中, 因为createRoom和joinRoom的逻辑不同, 所以需要分开处理
                const roomInfo = get().roomInfo();
                await rtm.joinRoom({
                    roomUUID,
                    uid: userUUID,
                    userSig: globalStore.rtcInfo.userSig,
                    roomName: roomInfo?.channelName || roomUUID,
                });
                const interval = queryResult.timeInterval;
                if (interval !== undefined && interval !== null) {
                    (rtm as any).messageInterval = interval;
                }
                console.log("[WEB] [init] DEBUG rtm加入房间后", rtm.members, get().isJoinedRTC);
                await initRTC();
                const ownerProfile = await rtm.getUserProfile(queryResult.ownerUuid);

                if (!get().isJoinedRTC) {
                    try {
                        await channelStore.joinChannel(roomUUID);
                    } catch (error) {
                        set({ isJoinedRTC: false, roomUUID: "", channelUsers: [] });
                        window.location.hash = "#/channel";
                        setTimeout(() => {
                            eventBus.emit("preview-camera", {
                                isPreview: false,
                            });
                        }, 0);
                        set({ isLoadingRTC: false });
                        return;
                    }
                }
                eventBus.emit("update-channels", undefined);
                set({
                    roomUUID,
                    ownerName: ownerProfile[0]?.nick,
                    channelName: queryResult.channelName,
                });
                const numResult = await channelStore.queryChannelNum(roomUUID);
                const channelUsers = [...numResult.data];
                set({ channelUsers });
                await users.initUsers([...channelUsers], [userUUID, ...channelUsers]);
                set({ isLoadingRTC: false, isJoinedRTC: true });
            } catch (error) {
                set({ isLoadingRTC: false, isJoinedRTC: false });
            }
        },

        joinRoom: async (roomUUID: string, roomInfo?: any) => {
            const { rtm, users, userUUID } = get();
            if (!rtm) {
                throw new Error("Missing RTM service");
            }
            if (!users) {
                throw new Error("Missing user store");
            }
            const queryResult = await channelStore.queryChannelInfo(roomUUID);
            if (!queryResult) {
                eventBus.emit("update-channels", undefined);
                eventBus.emit("show-message", {
                    message: (window as any).__ReMoI18n.t("channel-not-exist"),
                    type: "warning",
                });
                window.history.back();
                return;
            }
            const globalStore = useGlobalStore.getState();
            // 根据 isJoinedRTC 判断是否已加入房间
            users.initCurrentUser(userUUID);
            if (!get().isJoinedRTC && globalStore.rtcInfo) {
                console.log("正在加入房间", roomUUID);
                await rtm.joinRoom({
                    roomUUID,
                    uid: userUUID,
                    userSig: globalStore.rtcInfo.userSig || "",
                    roomName: roomInfo?.channelName || "测试房间",
                });
                set({ isJoinedRTC: true, roomUUID });
                console.log("成功加入房间", roomUUID);
            }
            const timeInterval = queryResult.timeInterval;
            if (timeInterval !== undefined && timeInterval !== null) {
                (rtm as any).messageInterval = timeInterval;
            }
            console.log("[WEB] [joinRoom] DEBUG rtm加入房间后", rtm.members);
            await initRTC();
            if (!get().isJoinedRTC) {
                try {
                    await channelStore.joinChannel(roomUUID);
                } catch (error) {
                    set({ isJoinedRTC: false, roomUUID: "", channelUsers: [] });
                    window.location.hash = "#/channel";
                    setTimeout(() => {
                        eventBus.emit("preview-camera", {
                            isPreview: false,
                        });
                    }, 0);
                    return;
                }
            }
            eventBus.emit("update-channels", undefined);
            const numResult = await channelStore.queryChannelNum(roomUUID);
            const channelUsers = [...numResult.data];
            set({ channelUsers });
            await users.initUsers([...channelUsers], [userUUID, ...channelUsers]);
        },

        // 离开房间
        leaveRoom: async () => {
            const { rtm, rtc, roomUUID, sideEffect } = get();
            if (rtm || rtc) {
                // 离开房间逻辑
                console.log("[WEB] [leaveRoom] debug rtm/rtc离开房间");
                rtc && (await rtc.leaveRoom());
                rtm && (await rtm.leaveRoom());
            }
            if (roomUUID) {
                await channelStore.quitChannel(roomUUID);
            }
            sideEffect.flushAll();
            set({ isJoinedRTC: false, roomUUID: "", channelUsers: [] });
        },

        // setDenoise: async (denoise: boolean) => {
        //     const { rtc } = get();
        //     if (!rtc) {
        //         throw new Error("Missing RTC service");
        //     }
        //     console.log("设置降噪", denoise);
        //     // await rtc.setAudioQuality({
        //     //     denoise,
        //     // });
        // },
        async destroy() {
            await this.leaveRoom();
            this.sideEffect.flushAll();
            (window as any).channelroomStore = null;
        },

        updateSpecialList(specialList) {
            set({ channelSpecialList: specialList });
        },
        updateIsEnterRoom(isEnterRoom) {
            set({ isEnterRoom });
        },
        updateAudioList(audioList) {
            // 根据 actionID 去重，保留最后一个出现的项（后出现的覆盖前一个）
            const uniqueMap = new Map<
                string,
                Pick<InterActionMessageType, "soundEffect" | "actionID">
            >();
            for (const a of audioList) {
                uniqueMap.set(a.actionID, a);
            }
            const uniqueList = Array.from(uniqueMap.values());
            set({ channelAudioList: uniqueList });
        },
        appendAudioList(audio) {
            // 先移除已有相同 actionID 的项，然后将新项追加（确保唯一性）
            const existing = get().channelAudioList.filter(
                item => item.actionID !== audio.actionID,
            );
            set({ channelAudioList: [...existing, audio] });
        },
        removeAudioList(audio) {
            const newAudio = get().channelAudioList.filter(
                item => item.actionID !== audio.actionID,
            );
            set({ channelAudioList: newAudio });
        },
    };
});

export const channelRoomStore = useChannelRoomStore.getState();
